// Chat state, held in a module rather than a component.
//
// The launcher panel and the #/chat screen are two views onto one conversation.
// Keeping the in-flight stream here means moving between them doesn't unmount
// it — React re-renders, the fetch keeps going. Same subscriber pattern as
// toast.tsx and router.ts; no state library.

import { api } from './api'
import type { ChatMessage, Conversation } from '../shared/types'

/** A write the model wants to make. Nothing has happened server-side yet — the
 *  tool only runs if this is approved. */
export interface Proposal {
  tool_use_id: string
  name: string
  summary: string
  input: Record<string, unknown>
  decision?: 'approved' | 'declined'
}

export interface ChatTurn {
  id: number            // 0 for an optimistic local turn not yet persisted
  role: 'user' | 'assistant'
  text: string
  tools: string[]       // tool names invoked during this turn
  proposals: Proposal[]
  complete: boolean
  streaming?: boolean
  costUsd?: number
}

export interface ChatModelOption {
  id: string
  label: string
  input: number
  output: number
  modern: boolean
  note: string
}

// Kept in sync with server/chat.ts, which is the authority — an unknown id
// there falls back to the default rather than erroring.
export const MODEL_OPTIONS: ChatModelOption[] = [
  { id: 'claude-opus-5', label: 'Opus 5', input: 5, output: 25, modern: true, note: 'Most capable. The default.' },
  { id: 'claude-sonnet-5', label: 'Sonnet 5', input: 3, output: 15, modern: true, note: 'Near-Opus quality, cheaper and faster.' },
  {
    id: 'claude-haiku-4-5',
    label: 'Haiku 4.5',
    input: 1,
    output: 5,
    modern: false,
    note: 'Cheapest. No thinking or effort control — best for simple lookups.',
  },
]
export const EFFORT_OPTIONS = ['low', 'medium', 'high', 'xhigh', 'max'] as const
export type ChatEffort = (typeof EFFORT_OPTIONS)[number]

interface ChatState {
  open: boolean
  conversationId: number | null
  clientId: number | null
  turns: ChatTurn[]
  sending: boolean
  error: string | null
  conversations: Conversation[]
  model: string
  effort: ChatEffort
  /** One-shot: the composer should start recording as soon as it mounts. */
  autoRecord: boolean
  /** Floating launcher dismissed. Ctrl+K and #/chat still work. */
  bubbleHidden: boolean
}

const PREFS_KEY = 'chat:prefs'
function loadPrefs(): { model: string; effort: ChatEffort; bubbleHidden: boolean } {
  try {
    const raw = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}')
    return {
      model: MODEL_OPTIONS.some((m) => m.id === raw.model) ? raw.model : MODEL_OPTIONS[0].id,
      effort: EFFORT_OPTIONS.includes(raw.effort) ? raw.effort : 'low',
      bubbleHidden: raw.bubbleHidden === true,
    }
  } catch {
    return { model: MODEL_OPTIONS[0].id, effort: 'low', bubbleHidden: false }
  }
}

const prefs = loadPrefs()

let state: ChatState = {
  open: false,
  conversationId: null,
  clientId: null,
  turns: [],
  sending: false,
  error: null,
  conversations: [],
  model: prefs.model,
  effort: prefs.effort,
  autoRecord: false,
  bubbleHidden: prefs.bubbleHidden,
}

type Listener = () => void
const listeners = new Set<Listener>()

function emit() {
  for (const l of listeners) l()
}
function set(patch: Partial<ChatState>) {
  state = { ...state, ...patch }
  emit()
}

export function subscribe(l: Listener): () => void {
  listeners.add(l)
  return () => listeners.delete(l)
}
export function getChatState(): ChatState {
  return state
}

// ── panel ───────────────────────────────────────────────────────────────────

export function openChat(clientId?: number | null) {
  set({ open: true, clientId: clientId ?? state.clientId })
}
export function closeChat() {
  set({ open: false })
}
export function toggleChat(clientId?: number | null) {
  if (state.open) closeChat()
  else openChat(clientId)
}

/** Touch device — the case where reaching for the mic is the point, and where
 *  typing is the slow path. Pointer coarseness beats a width breakpoint: it
 *  tracks the input device rather than the window. */
export function isTouchDevice(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches
}

export function requestAutoRecord() {
  set({ autoRecord: true })
}

/** Read and clear, so the composer can't start recording twice. */
export function consumeAutoRecord(): boolean {
  if (!state.autoRecord) return false
  set({ autoRecord: false })
  return true
}

// ── model / effort ──────────────────────────────────────────────────────────
// Sticky rather than per-message: bump it for a hard question, and it stays
// until changed back.

function savePrefs(patch: Partial<{ model: string; effort: ChatEffort; bubbleHidden: boolean }> = {}) {
  const next = {
    model: state.model,
    effort: state.effort,
    bubbleHidden: state.bubbleHidden,
    ...patch,
  }
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(next))
  } catch {
    /* private mode / quota — the setting just won't survive a reload */
  }
}

export function setModel(id: string) {
  const model = MODEL_OPTIONS.some((m) => m.id === id) ? id : MODEL_OPTIONS[0].id
  set({ model })
  savePrefs({ model })
}

export function setEffort(effort: ChatEffort) {
  set({ effort })
  savePrefs({ effort })
}

/** Hiding the launcher never strands the assistant: Ctrl+K still opens it, the
 *  sidebar's Chat tab still works, and the chat screen offers it back. */
export function setBubbleHidden(bubbleHidden: boolean) {
  set({ bubbleHidden })
  savePrefs({ bubbleHidden })
}

// ── conversations ───────────────────────────────────────────────────────────

/** Extract display text and tool names from a stored content block array. */
function renderStored(m: ChatMessage): ChatTurn {
  let text = ''
  const tools: string[] = []
  try {
    const blocks = JSON.parse(m.content_json) as any[]
    for (const b of blocks) {
      if (b.type === 'text') text += b.text
      else if (b.type === 'tool_use') tools.push(b.name)
    }
  } catch {
    /* malformed row — render as empty rather than breaking the thread */
  }
  // Proposals aren't reconstructed from history: a reloaded thread shows what
  // was proposed as a tool name, but the confirm affordance is live-only. A
  // stale Confirm button after a reload would be worse than none.
  return { id: m.id, role: m.role, text, tools, proposals: [], complete: m.complete === 1 }
}

export async function refreshConversations() {
  try {
    set({ conversations: await api.listConversations() })
  } catch {
    /* list is a convenience; failure shouldn't break the panel */
  }
}

export async function loadConversation(id: number) {
  set({ conversationId: id, turns: [], error: null })
  try {
    const { conversation, messages } = await api.getConversation(id)
    set({
      conversationId: conversation.id,
      clientId: conversation.client_id,
      turns: messages.map(renderStored),
    })
  } catch (e) {
    set({ error: (e as Error).message })
  }
}

export function newConversation(clientId?: number | null) {
  set({ conversationId: null, clientId: clientId ?? null, turns: [], error: null })
}

export async function archiveConversation(id: number) {
  await api.updateConversation(id, { archived: 1 })
  if (state.conversationId === id) newConversation(state.clientId)
  await refreshConversations()
}

export async function renameConversation(id: number, title: string) {
  await api.updateConversation(id, { title })
  await refreshConversations()
}

// ── sending ─────────────────────────────────────────────────────────────────

/** Describes the screen the user is on, so "log two hours on this" resolves.
 *  Delivered as a mid-conversation system message server-side. */
export type RouteContextFn = () => string | undefined
let routeContext: RouteContextFn = () => undefined
export function setRouteContextProvider(fn: RouteContextFn) {
  routeContext = fn
}

let lastSentContext: string | undefined

/** Record a decision on a proposal. When every proposal in the turn has been
 *  decided, the turn resumes automatically — the model then sees the results. */
export function decideProposal(toolUseId: string, approved: boolean) {
  const turns = state.turns.map((t) => ({
    ...t,
    proposals: t.proposals.map((p) =>
      p.tool_use_id === toolUseId ? { ...p, decision: approved ? ('approved' as const) : ('declined' as const) } : p,
    ),
  }))
  set({ turns })

  const last = turns[turns.length - 1]
  if (!last || last.proposals.length === 0) return
  if (last.proposals.some((p) => !p.decision)) return // still waiting on others

  void resume(
    last.proposals.map((p) => ({ tool_use_id: p.tool_use_id, approved: p.decision === 'approved' })),
  )
}

async function resume(decisions: { tool_use_id: string; approved: boolean }[]) {
  await run({ confirm: { decisions } })
}

export async function sendMessage(text: string) {
  const trimmed = text.trim()
  if (!trimmed || state.sending) return

  const ctx = routeContext()
  // Only resend context when it actually changed — an unchanged block would be
  // pure noise in the history and would push the cached prefix around.
  const contextToSend = ctx && ctx !== lastSentContext ? ctx : undefined
  if (contextToSend) lastSentContext = contextToSend

  set({
    turns: [
      ...state.turns,
      { id: 0, role: 'user', text: trimmed, tools: [], proposals: [], complete: true },
    ],
  })

  await run({ message: trimmed, route_context: contextToSend })
}

/** One streamed turn. Shared by a new user message and by resuming after a
 *  proposal decision — the wire shape is the same either way. */
async function run(payload: Record<string, unknown>) {
  if (state.sending) return

  set({
    sending: true,
    error: null,
    turns: [
      ...state.turns,
      { id: 0, role: 'assistant', text: '', tools: [], proposals: [], complete: false, streaming: true },
    ],
  })

  const patchLast = (patch: Partial<ChatTurn>) => {
    const turns = [...state.turns]
    const i = turns.length - 1
    if (i >= 0) turns[i] = { ...turns[i], ...patch }
    set({ turns })
  }

  try {
    const res = await fetch('/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        conversation_id: state.conversationId ?? undefined,
        client_id: state.clientId,
        model: state.model,
        effort: state.effort,
        ...payload,
      }),
    })

    if (!res.ok || !res.body) {
      const body = await res.json().catch(() => null)
      const msg =
        (body && typeof body === 'object' && 'message' in body && String((body as any).message)) ||
        `Chat failed (${res.status})`
      patchLast({ streaming: false, complete: false })
      set({ error: msg, sending: false })
      return
    }

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const chunks = buffer.split('\n\n')
      buffer = chunks.pop() ?? ''
      for (const chunk of chunks) {
        const line = chunk.trim()
        if (!line.startsWith('data:')) continue
        let ev: any
        try {
          ev = JSON.parse(line.slice(5).trim())
        } catch {
          continue
        }
        const last = state.turns[state.turns.length - 1]
        if (ev.type === 'text') patchLast({ text: (last?.text ?? '') + ev.text })
        else if (ev.type === 'tool') patchLast({ tools: [...(last?.tools ?? []), ev.name] })
        else if (ev.type === 'proposal') {
          patchLast({
            proposals: [
              ...(last?.proposals ?? []),
              { tool_use_id: ev.tool_use_id, name: ev.name, summary: ev.summary, input: ev.input },
            ],
          })
        } else if (ev.type === 'done') {
          patchLast({ streaming: false, complete: true, id: ev.message_id, costUsd: ev.cost_usd })
          set({ conversationId: ev.conversation_id })
          void refreshConversations()
        } else if (ev.type === 'error') {
          patchLast({ streaming: false, complete: false })
          set({ error: ev.message })
        }
      }
    }
    // A turn that paused for confirmation never sends `done`.
    patchLast({ streaming: false })
  } catch (e) {
    patchLast({ streaming: false, complete: false })
    set({ error: (e as Error).message })
  } finally {
    set({ sending: false })
  }
}
